import { GetEvents } from 'components/hmilib/api';
import React, { useEffect, useState } from 'react';
import Table, { IColum } from '../../Table/Table';
import PortableWindow from '../PortableWindow';
import HRTabButtons from './components/HRTabButtons/HRTabButtons';
import SearchCondition from './components/SearchCondition/SearchCondition';
import { Config } from './components/SearchCondition/searchConditionInterface';
import './historical-report.css';

export interface RootInterface {
    Items: Event[];
    MaxPage: number;
    MaxItem: number;
}

export interface Event {
    ID: string;
    EventType: number;
    EventCategory: number;
    SourceName: string;
    Time: string;
    Message: string;
    ClassMessage?: any;
    Severity: number;
    Block: string;
    Param: string;
    Station: string;
    Cookie: number;
    SourceSystem?: any;
    Namespace?: any;
    Application: string;
    Attr1?: any;
    Attr2?: any;
    AttrInt1: number;
    AttrInt2: number;
    ConditionName?: any;
    ClientUserId: string;
    NewState: number;
    ClientUserFullName: string;
    PrevValue: string;
    Value: string;
    EU?: any;
}

interface IHistoricalReport {
    index: number;
    item: any;
    CloseSettings: () => void;
    SelectActiveTab: (id: number) => void;
}

const currentDate = new Date();

const oneDayBefore = new Date(currentDate);
oneDayBefore.setDate(currentDate.getDate() - 1);

function formatTimeWithoutSeconds(timeString: any) {
    let [timePart, secondsPart] = timeString.split(':');
    return `${timePart}:${secondsPart}`;
}

const config: Config = {
    opccurrenceSource: {
        all: true,
        specifyOccurrenceSource: {
            state: false,
            stationName: {
                active: false,
                params: ['', '']
            },
            hierarchyName: {
                active: false,
                params: ['', '']
            },
            tagName: {
                active: false,
                params: ['', '']
            },
            batchID: {
                active: false,
                params: ['', '']
            }
        },
        setOccurrenceSource(
            type: 'stationName' | 'hierarchyName' | 'tagName' | 'batchID' | 'state',
            params: 'active' | 'params',
            index: number,
            value: any
        ) {
            if (type != 'state') {
                if (params == 'active') {
                    this.specifyOccurrenceSource[type][params] = value;
                } else {
                    this.specifyOccurrenceSource[type][params][index] = value;
                }
            }
        },
        select(mode: string) {
            this.all = mode === 'all';
            this.specifyOccurrenceSource.state = mode === 'specifyOccurrenceSource';
        }
    },
    user: {
        all: true,
        specifyUser: {
            state: false,
            data: ['', ''],
            users: []
        },
        select(mode: string) {
            this.all = mode === 'all';
            this.specifyUser.state = mode === 'specifyUser';
        }
    },
    aribitraryCharacher: {
        noCheck: true,
        check: {
            state: false,
            data: ['', '']
        },
        select(mode: string) {
            this.noCheck = mode === 'noCheck';
            this.check.state = mode === 'check';
        }
    },
    date: {
        all: true,
        specifiedDate: {
            state: false,
            between: {
                date: currentDate.toISOString().split('T')[0],
                time: formatTimeWithoutSeconds(currentDate.toTimeString().split(' ')[0])
            },
            and: {
                date: oneDayBefore.toISOString().split('T')[0],
                time: formatTimeWithoutSeconds(currentDate.toTimeString().split(' ')[0])
            }
        },
        past: {
            state: false,
            number: 0,
            format: 'hours'
        },
        select(mode: string) {
            this.all = mode === 'all';
            this.specifiedDate.state = mode === 'specifiedDate';
            this.past.state = mode === 'past';
        }
    },
    messageType: {
        all: true,
        specifyMessageType: {
            state: false,
            types: {
                processAlarm: false,
                statusChange: false,
                operationGuideMessage: false,
                sequenceMessage: false,
                maintenance: false,
                operationMessage: false,
                systemAlarm: false,
                fieldbus: false
            },
            setType(type: keyof typeof this.types, value: boolean) {
                this.types[type] = value;
            }
        },
        specifyNumber: {
            state: false,
            messageNo: ['', '', '', '']
        },
        select(mode: string) {
            this.all = mode === 'all';
            this.specifyMessageType.state = mode === 'specifyMessageType';
            this.specifyNumber.state = mode === 'specifyNumber';
        }
    },
    direction: {
        backward: true,
        forward: false,
        select(mode: string) {
            this.backward = mode === 'backward';
            this.forward = mode === 'forward';
        }
    },
    display: {
        all: true,
        search: false,
        select(mode: string) {
            this.all = mode === 'all';
            this.search = mode === 'search';
        }
    }
};

function HistoricalReport(props: IHistoricalReport) {
    const { index, item, CloseSettings, SelectActiveTab } = props;

    const [data, setData] = useState<Event[]>([]);
    const [historicalState, setHistoricalState] = useState<string>('Ready');
    const [countEvent, setCountEvent] = useState<number>(0);
    const [searchConditionRender, setSearchConditionRender] = useState<boolean>(false);

    const [tempConfig, setTempConfig] = useState<Config>(config);

    const handleMouseClick = (event: React.MouseEvent<HTMLDivElement>) => {
        if (!item.active) SelectActiveTab(index);
    };

    const header: IColum[] = [
        {
            type: 'text',
            width: '10%',
            label: 'Message No',
            idCol: 'MsgNum',
            action: false
        },
        {
            type: 'text',
            width: '20%',
            label: 'Date',
            idCol: 'Time',
            action: false
        },
        {
            type: 'text',
            width: '70%',
            label: 'Message Text',
            idCol: 'Message',
            action: false
        }
    ];

    function OpenSearchCondition() {
        setSearchConditionRender(true);
    }

    function CloseSearchCondition() {
        setSearchConditionRender(false);
    }

    function GetHistoricalEvents(config: Config = tempConfig) {
        const sort_col = '';
        const sort_up = false;
        const select_page = 0;
        const limit = 0;
        const filterItems = {
            // Name: 'def',
            // Value: [
            //     { Name: 'ID', Value: ['3ea286a2-2350-47e2-a383-9a536be38c0d'], Oper: 'eq' },
            //     { Name: 'ID', Value: ['ce96b356-6403-4f29-803b-f8751e9a6817'], Oper: 'eq' },
            //     { Name: 'ID', Value: ['491c0c80-4c65-4f41-8d10-a4472fb33a80'], Oper: 'eq' }
            // ],
            // Oper: 'or'
        };

        const sortItems = JSON.stringify(filterItems);
        // const sortItems = '';
        const users = new Set();
        users.add('');

        GetEvents(sort_col, sort_up, sortItems, select_page, limit)
            .then((data: any) => {
                data.Items.forEach((item: any) => {
                    const dateEvent = new Date(item.Time);
                    item.originalTime = dateEvent;

                    const date = dateEvent.toLocaleDateString('ru-RU').replace(/\./g, '/');

                    const timeOptions: Intl.DateTimeFormatOptions = {
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                        hour12: true
                    };
                    const time = dateEvent.toLocaleTimeString('en-US', timeOptions);

                    if (item.MsgNum == 0) {
                        const { newMsgNum, newMessage } = formatMessage(item);
                        if (newMessage) {
                            item.MsgNum = newMsgNum;
                            item.Message = newMessage;
                        }
                    }

                    item.Time = `${date} ${time}`;
                    if (item.ClientUserFullName && item.ClientUserFullName != null) {
                        users.add(item.ClientUserFullName);
                    }
                });

                const FilterData = SearchFilter(data, config);

                config.user.specifyUser.users = Array.from(users) as string[];

                setTempConfig(config);
                setCountEvent(FilterData.Items.length);
                setData(FilterData.Items);
            })
            .catch((error: any) => console.log(error));
    }

    useEffect(() => {
        GetHistoricalEvents(tempConfig);
    }, []);

    function handleSearch(config: Config) {
        // setTempConfig((prevConfig) => ({ ...config }));
        GetHistoricalEvents(config);
    }

    function SearchFilter(data: any, config: Config): any {
        const newData = JSON.parse(JSON.stringify(data));

        newData.Items = sortByTime(newData.Items, config.direction.backward ? 'backward' : 'forward');

        // Фильтрация
        if (config.display.search) {
            if (config.messageType.all) {
            } else {
                if (config.messageType.specifyMessageType.state) {
                    const eventCategories = getEventCategories(config.messageType.specifyMessageType.types);
                    newData.Items = filterByEventCategory(newData.Items, eventCategories);
                } else if (config.messageType.specifyNumber.state) {
                    newData.Items = filterByProperty(
                        newData.Items,
                        config.messageType.specifyNumber.messageNo,
                        'MsgNum'
                    );
                }
            }
            if (config.opccurrenceSource.all) {
            } else {
                if (config.opccurrenceSource.specifyOccurrenceSource.state) {
                    if (config.opccurrenceSource.specifyOccurrenceSource.stationName.active) {
                    }
                    if (config.opccurrenceSource.specifyOccurrenceSource.stationName.active) {
                        const TagNames = config.opccurrenceSource.specifyOccurrenceSource.stationName.params;
                        newData.Items = filterByProperty(newData.Items, TagNames, 'Station');
                    }
                    if (config.opccurrenceSource.specifyOccurrenceSource.tagName.active) {
                        const TagNames = config.opccurrenceSource.specifyOccurrenceSource.tagName.params;
                        newData.Items = filterByProperty(newData.Items, TagNames, 'Block');
                    }
                }
            }
            if (!config.user.all) {
                if (config.user.specifyUser.state) {
                    const Names = config.user.specifyUser.data;
                    newData.Items = filterByProperty(newData.Items, Names, 'ClientUserFullName');
                }
            }
            if (!config.date.all) {
                if (config.date.specifiedDate.state) {
                    const between = config.date.specifiedDate.between;
                    const and = config.date.specifiedDate.and;
                    const startDate = new Date(`${and.date} ${and.time}`);
                    const endDate = new Date(`${between.date} ${between.time}`);
                    newData.Items = specifiedDate(newData.Items, endDate, startDate);
                }
                if (config.date.past.state) {
                    const number = config.date.past.number;
                    const format = config.date.past.format;
                    const between = new Date();
                    const and = getDateWithDifference(format, number);

                    newData.Items = specifiedDate(newData.Items, between, and);
                }
            }
        }

        return newData;
    }

    function getDateWithDifference(unit: string, value: number) {
        const now = new Date();

        const resultDate = new Date(now);

        switch (unit.toLowerCase()) {
            case 'minutes':
                resultDate.setMinutes(now.getMinutes() - value);
                break;
            case 'hours':
                resultDate.setHours(now.getHours() - value);
                break;
            case 'days':
                resultDate.setDate(now.getDate() - value);
                break;
            case 'weeks':
                resultDate.setDate(now.getDate() - value * 7);
                break;
            default:
                break;
        }

        return resultDate;
    }

    function specifiedDate(items: any[], between: any, and: any) {
        function isWithinRange(Time: any) {
            const itemDate = new Date(Time);
            return itemDate >= and && itemDate <= between;
        }

        return items.filter((item: any) => isWithinRange(item.originalTime));
    }

    function filterByProperty(
        items: any[],
        messageNo: string[],
        property: 'Block' | 'MsgNum' | 'Station' | 'ClientUserFullName'
    ) {
        messageNo = messageNo.filter((No) => No !== '');

        return items.filter((item: any) => {
            return messageNo.some((No) => {
                if (No.includes('*')) {
                    const pattern = new RegExp('^' + No.replace(/\*/g, '.*'));
                    if (item[property] && item[property] != null) {
                        return pattern.test(item[property].toString());
                    } else {
                        return false;
                    }
                } else {
                    if (item[property] && item[property] != null) {
                        return No.includes(item[property].toString());
                    } else {
                        return false;
                    }
                }
            });
        });
    }

    function getEventCategories(types: Record<string, boolean>): number[] {
        const eventCategories: Record<string, number> = {
            processAlarm: 102,
            operationGuideMessage: 105,
            systemAlarm: 101
        };

        return Object.entries(types)
            .filter(([key, value]) => value && eventCategories[key] !== undefined)
            .map(([key]) => eventCategories[key]);
    }

    function filterByEventCategory(items: [], EventCategories: number[]) {
        return items.filter((item: any) => EventCategories.includes(item.EventCategory));
    }

    function sortByTime(items: any[], direction: 'forward' | 'backward') {
        return items.sort((a: any, b: any) => {
            const timeA = new Date(a.originalTime).getTime();
            const timeB = new Date(b.originalTime).getTime();

            if (direction === 'forward') {
                return timeA - timeB;
            } else {
                return timeB - timeA;
            }
        });
    }

    function formatMessage(item: any) {
        let newMsgNum: number = 0;
        let newMessage: any = '';
        const Recover = ((item.InitState >> 0) & 1) == 0;
        if (item.Block) {
            const blockSplit = item.Block.split('/');
            const Block = AddSpaces(blockSplit[blockSplit.length - 1], 15);
            const BlockDesc = AddSpaces(item.BlockDesc, 25);
            const Param = AddSpaces(item.Param.split('.')[0], 4);
            const Value = AddSpaces(item.Value, 8, 'start');
            const EU = AddSpaces(item.EU, 0);
            const StatusName = AddSpaces(item.StatusName, 0);

            function AddSpaces(value: string, count: number, position: 'start' | 'end' = 'end'): string {
                if (value != null && value != undefined) {
                    return position === 'end'
                        ? value.slice(0, count).padEnd(count)
                        : value.slice(0, count).padStart(count);
                } else {
                    return position == 'end' ? ''.padEnd(count) : ''.padStart(count);
                }
            }

            if (item.EventCategory === 102 && item.EventType === 4) {
                if (item.StatusName === 'NR') {
                    newMsgNum = 1106;
                    newMessage = `${Block} ${BlockDesc} ${Param}`;
                } else {
                    const paramValue = `${Param} = ${Value} ${EU}`;
                    if (item.Param === 'MV') {
                        newMsgNum = Recover ? 1104 : 1103;
                        newMessage = `${Block} ${BlockDesc} ${paramValue} ${StatusName} ${Recover ? 'Recover' : ''}`;
                    } else if (item.Param === 'PV') {
                        newMsgNum = Recover ? 1101 : 1102;
                        newMessage = `${Block} ${BlockDesc} ${paramValue} ${StatusName} ${Recover ? 'Recover' : ''}`;
                    }
                }
            } else if (item.EventCategory === 107 && item.EventType === 2) {
                newMsgNum = !Number.isNaN(item.Value) ? 1601 : 1608;

                const Value = AddSpaces(item.Value ? item.Value.substr(0, 8) : '', 8, 'start');
                const PrevValue = `old = ${item.PrevValue}`;
                const ClientStation = `[${item.ClientUserFullName}@${item.Station}]`;

                const messageParts = [Block, BlockDesc, `${Param} = ${Value}`, EU, PrevValue, ClientStation].filter(
                    Boolean
                );

                newMessage = messageParts.join(' ');
            } else if (item.EventCategory === 105 && item.EventType === 1) {
                newMsgNum = 1701;
                newMessage = BlockDesc;
            }

            return { newMsgNum, newMessage };
        } else {
            return { newMsgNum, undefined };
        }
    }

    const calculatePosition = (windowWidth: number, windowHeight: number) => {
        return {
            top: (window.innerHeight - windowHeight) / 2,
            left: (window.innerWidth - windowWidth) / 2,
            width: windowWidth,
            height: windowHeight
        };
    };

    const { top: topHR, left: leftHR, width: HRWidth, height: HRHeight } = calculatePosition(1250, 800);
    const { top: topSearch, left: leftSearch, width: SearchWidth, height: SearchHeight } = calculatePosition(500, 400);

    return (
        <div onMouseDown={handleMouseClick}>
            <PortableWindow
                active={item.active}
                width={HRWidth}
                height={HRHeight}
                Top={topHR}
                Left={leftHR}
                title="Historical Report - [Operation and Monitoring Message]"
                OnClose={CloseSettings}>
                <>
                    <HRTabButtons SearchCondition={OpenSearchCondition} Redraw={GetHistoricalEvents} />
                    <div className="historical-report__trends">
                        <Table
                            header={header}
                            data={data}
                            propRowHeight={20}
                            width="100%"
                            height="100%"
                            cssClassHead="header__trends"
                            cssClassBody="body__trends"
                            isFullRender={false}
                        />
                    </div>
                    <div className="footer">
                        <div className="footer__state">{historicalState}</div>
                        <div className="footer__Items">{countEvent} Items Found</div>
                    </div>
                </>
            </PortableWindow>
            {searchConditionRender && (
                <PortableWindow
                    active={item.active}
                    width={SearchWidth}
                    height={SearchHeight}
                    Top={topSearch}
                    Left={leftSearch}
                    title="Search"
                    OnClose={CloseSearchCondition}>
                    <>
                        <SearchCondition
                            config={tempConfig}
                            CloseSearchCondition={CloseSearchCondition}
                            handleSearch={handleSearch}
                        />
                    </>
                </PortableWindow>
            )}
        </div>
    );
}

export default HistoricalReport;
